/**
 * WorkspaceSettingsService — workspace_settings 테이블 CRUD
 *
 * 미설정 워크스페이스(row 없음) 는 defaults 로 채워 반환 — 호출자가 if-null 분기 안 해도 됨.
 */
import { supabase } from './SupabaseClient';
import {
  WorkspaceSettings, defaultWorkspaceSettings, rowToWorkspaceSettings,
} from '../models/WorkspaceSettings';

function getClient() {
  if (!supabase) throw new Error('[WorkspaceSettings] Supabase 클라이언트가 초기화되지 않았습니다.');
  return supabase;
}

/** 워크스페이스 설정 조회 — row 없으면 defaults 반환 (caller 가 분기 안 해도 됨) */
export async function fetchWorkspaceSettings(workspaceId: string): Promise<WorkspaceSettings> {
  const sb = getClient();
  const { data, error } = await sb
    .from('workspace_settings')
    .select('*')
    .eq('workspace_id', workspaceId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return defaultWorkspaceSettings(workspaceId);
  return rowToWorkspaceSettings(data);
}

/** 워크스페이스 설정 갱신 — upsert (없으면 insert, 있으면 부분 update) */
export async function updateWorkspaceSettings(
  workspaceId: string,
  patch: Partial<Omit<WorkspaceSettings, 'workspace_id' | 'updated_at'>>,
): Promise<WorkspaceSettings> {
  const sb = getClient();
  // 기존 row 가져와서 patch 와 머지 (jsonb / array 안전한 부분 갱신)
  const current = await fetchWorkspaceSettings(workspaceId);
  const merged: WorkspaceSettings = {
    workspace_id: workspaceId,
    board_defaults: patch.board_defaults
      ? { ...current.board_defaults, ...patch.board_defaults }
      : current.board_defaults,
    board_manager_user_ids: patch.board_manager_user_ids ?? current.board_manager_user_ids,
    gigapod: patch.gigapod
      ? { ...current.gigapod, ...patch.gigapod }
      : current.gigapod,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await sb
    .from('workspace_settings')
    .upsert(
      {
        workspace_id: workspaceId,
        board_defaults: merged.board_defaults as any,
        board_manager_user_ids: merged.board_manager_user_ids,
        gigapod: merged.gigapod as any,
        updated_at: merged.updated_at,
      },
      { onConflict: 'workspace_id' },
    )
    .select()
    .single();
  if (error) throw error;
  return rowToWorkspaceSettings(data);
}
